import {ChangeDetectionStrategy, Component, computed, inject, Input, Signal} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {Store} from '@ngrx/store';
import {StockItem, VatRates} from '../../../../shared-utilities/models-old/datastructures';
import {IPriceBand} from '../../../shared-models/price-banding/price-band';
import {IMultiplePriceChangeResult, IMultiplePriceChangeRulesStoreSettings} from '../../../../features/settings-new/models';
import {
  applyMultiplePriceChangesToStockItem,
  createDefaultMultiplePriceChangeSettings,
  resolveVatPercentage,
} from '../../../shared-utils/price/multiple-price-change.utils';
import {calculateGPValue} from '../../../shared-utils/calculations/calculations-stock';
import {
  selectSelectedStoreMultiplePriceChangeRules,
} from '../../../../features/settings-new/store/setting.selectors';
import {
  selectPriceBandingForUserSelectedStore,
  selectSharedGridVatRateBySelectedStore,
} from '../../../../shared-modules/shared-grid/store/shared-grid.selectors';
import {ModalControllerService} from '../../../shared-services/overlay/modal-controller.service';
import {IconColourEnum} from '../../../../shared-modules/shared-icons/models/icons-colour.enum';
import {IconsEnum} from '../../../../shared-modules/shared-icons/models/icons.enum';

export const SHARED_MULTIPLE_PRICE_CHANGE_MODAL_CSS = 'shared-multiple-price-change-modal-css';

/**
 * Shows every sell price that the store's multiple price change rules derive from retail price 1
 * for a single stock item, before the edits are submitted.
 */
@Component({
  selector: 'app-shared-multiple-price-change-modal',
  templateUrl: './shared-multiple-price-change-modal.component.html',
  styleUrls: ['./shared-multiple-price-change-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SharedMultiplePriceChangeModalComponent {

  @Input() stockItem: StockItem | null = null;

  protected readonly iconColourEnum = IconColourEnum;
  protected readonly icons = IconsEnum;

  private readonly store = inject(Store);
  private readonly modalController = inject(ModalControllerService);

  private readonly settings: Signal<IMultiplePriceChangeRulesStoreSettings> = toSignal(
    this.store.select(selectSelectedStoreMultiplePriceChangeRules),
    {initialValue: createDefaultMultiplePriceChangeSettings()},
  );

  private readonly vatRates: Signal<VatRates> = toSignal(
    this.store.select(selectSharedGridVatRateBySelectedStore),
    {initialValue: {} as VatRates},
  );

  private readonly priceBands: Signal<IPriceBand[]> = toSignal(
    this.store.select(selectPriceBandingForUserSelectedStore),
    {initialValue: []},
  );

  protected readonly results: Signal<IMultiplePriceChangeResult[]> = computed(() => {
    if (!this.stockItem) {
      return [];
    }
    return applyMultiplePriceChangesToStockItem(
      this.stockItem,
      this.settings(),
      this.vatRates(),
      this.priceBands() ?? [],
    ).results;
  });

  protected readonly latestCost: Signal<number> = computed(() => Number(this.stockItem?.latestCost ?? 0));

  protected readonly sellPriceIncluding1: Signal<number> = computed(() => Number(this.stockItem?.sellPriIncl1 ?? 0));

  protected readonly priceOneGp: Signal<number> = computed(() => {
    const vatPercentage: number = resolveVatPercentage(Number(this.stockItem?.vatR ?? 0), this.vatRates());
    return calculateGPValue(vatPercentage, this.sellPriceIncluding1(), this.latestCost());
  });

  protected describeResult(result: IMultiplePriceChangeResult): string {
    const source: string = result.mode === 'fixed'
      ? `Fixed GP of ${result.targetGp}%`
      : `Price 1 GP adjusted to ${result.targetGp}%`;
    return result.priceBandingApplied ? `${source}, price banded` : source;
  }

  protected onClose(): void {
    void this.modalController.dismiss();
  }
}
